/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/services/api/sopComplianceService.ts
 * PURPOSE: SOP Compliance Analytics API Service - Dashboard, KPIs, Trends
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * DATE: 2026-01-26
 * COMPLIANCE: SDLC 6.3.1 + Vietnamese Labor Law 2019 + BHXH 2025
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance - Rule 7: API Contract)
 * SPRINT GOVERNANCE: Rule 7: Performance SLAs (<50ms p95)
 *
 * @business-context
 * - Domain: SOP Compliance Analytics, KPI Tracking, Vietnamese BPM
 * - Impact: Revenue Critical (₫38B TAM), Multi-Industry Operations
 * - Stakeholders: Operations Team, Management, Local Team (Vietnam)
 *
 * @technical-architecture
 * - Layer: Data Access Layer, API Services
 * - Pattern: Service Pattern, Axios Client, Type-Safe API
 * - Dependencies: Axios, TypeScript, Backend SOP Analytics APIs
 *
 * @cross-references
 * - Design: docs/02-design/10-bpm-architecture/SOP-IMPORT-COMPLIANCE-KPI-DESIGN.md
 * - API: backend/app/api/v1/sop_analytics.py
 * - Training: docs/07-operate/02-Training/SOP-BPM-WFM-TRAINING-GUIDE.md
 */

import { apiClient } from './client';

// ============= Industry Constants (Vietnamese) =============

export const INDUSTRY_NAMES_VI: Record<string, string> = {
  'F&B': 'Thực phẩm & Đồ uống',
  'HOSPITALITY': 'Khách sạn & Nhà nghỉ',
  'CLEANING': 'Dịch vụ Vệ sinh',
  'NURSING': 'Chăm sóc Điều dưỡng',
  'HEALTHCARE': 'Y tế',
  'GLOBAL': 'Toàn ngành',
};

export const KPI_CATEGORY_NAMES_VI: Record<string, string> = {
  'TIME': 'Thời gian',
  'QUALITY': 'Chất lượng',
  'COST': 'Chi phí',
  'COMPLIANCE': 'Tuân thủ',
  'EFFICIENCY': 'Hiệu suất',
};

export const KPI_UNIT_NAMES_VI: Record<string, string> = {
  'minutes': 'phút',
  'hours': 'giờ',
  'percent': 'phần trăm',
  'count': 'số lượng',
  'VND': 'VND',
};

// ============= Type Definitions =============

export interface SOPStatistics {
  total_sops: number;
  active_sops: number;
  by_category: Record<string, number>;
  by_industry: Record<string, number>;
}

export interface ComplianceMetrics {
  total_checks: number;
  pass_count: number;
  fail_count: number;
  pending_count: number;
  waived_count: number;
  pass_rate: number;
  fail_rate: number;
}

export interface ViolationSummary {
  critical: number;
  high: number;
  medium: number;
  low: number;
  total_violations: number;
}

export interface ComplianceDashboardResponse {
  compliance_score: number;
  sop_statistics: SOPStatistics;
  compliance_metrics: ComplianceMetrics;
  violations: ViolationSummary;
  last_updated: string;
}

export interface TrendDataPoint {
  date: string;
  pass_count: number;
  fail_count: number;
  total_count: number;
  pass_rate: number;
}

export interface TrendSummary {
  total_checks: number;
  total_pass: number;
  total_fail: number;
  avg_pass_rate: number;
}

export interface ComplianceTrendsResponse {
  period_days: number;
  start_date: string;
  end_date: string;
  daily_data: TrendDataPoint[];
  summary: TrendSummary;
}

export interface IndustryCoverage {
  industry: string;
  industry_vi: string;
  sop_count: number;
  coverage_percent: number;
}

export interface IndustryCoverageResponse {
  industries: IndustryCoverage[];
  total_industries: number;
}

export interface SOPPerformer {
  sop_id: string;
  sop_code: string;
  name_vi: string;
  category: string | null;
  industry: string | null;
  total_checks: number;
  pass_count: number | null;
  fail_count: number | null;
  pass_rate: number | null;
  fail_rate: number | null;
}

export interface KPISummary {
  total_kpis: number;
  by_category: Record<string, number>;
  by_unit: Record<string, number>;
  avg_target: number;
  sops_with_kpis: number;
}

export interface SOPKPIMapping {
  id: string;
  sop_template_id: string;
  sop_step_id: number | null;
  kpi_code: string;
  kpi_name_vi: string;
  kpi_name_en: string | null;
  kpi_description: string | null;
  target_value: number;
  threshold_warning: number | null;
  threshold_critical: number | null;
  measurement_unit: string;
  kpi_category: string | null;
  aggregation_method: string;
  measurement_frequency: string;
  is_active: boolean;
  effective_from: string | null;
  effective_to: string | null;
}

export interface KPIPerformance {
  kpi_code: string;
  kpi_name_vi: string;
  kpi_name_en: string | null;
  target_value: number;
  threshold_warning: number | null;
  threshold_critical: number | null;
  measurement_unit: string;
  actual_value: number | null;
  status: string;
  deviation_percent: number | null;
  data_points: number;
}

export interface SOPImportRequest {
  sops: Array<{
    sop_code: string;
    name_vi: string;
    name_en?: string;
    category: string;
    industry?: string;
    master_process?: string;
    version?: string;
    steps: Array<{
      step_id: number;
      name_vi: string;
      name_en?: string;
      evidence_required?: string[];
      estimated_duration_minutes?: number;
      is_mandatory?: boolean;
    }>;
    kpis?: Array<{
      kpi_code: string;
      name_vi: string;
      target: number;
      threshold?: number;
      unit?: string;
    }>;
  }>;
  filename?: string;
}

export interface SOPImportResponse {
  id: string;
  import_batch_id: string;
  sop_count: number;
  success_count: number;
  error_count: number;
  status: 'COMPLETED' | 'PARTIAL' | 'FAILED';
  processing_time_ms: number;
  errors?: Array<{
    line: number;
    sop_code: string;
    field: string;
    error: string;
    message_vi: string;
  }>;
}

export interface SOPValidateResponse {
  total_count: number;
  valid_count: number;
  error_count: number;
  warning_count: number;
  can_import: boolean;
  errors: Array<{
    line: number;
    sop_code: string;
    field: string;
    error: string;
    message_vi: string;
  }>;
  warnings: Array<{
    line: number;
    sop_code: string;
    field: string;
    warning: string;
    message_vi: string;
  }>;
}

export interface SOPImportHistory {
  id: string;
  import_batch_id: string;
  filename: string | null;
  sop_count: number;
  success_count: number;
  error_count: number;
  status: string;
  started_at: string;
  completed_at: string | null;
}

// ============= SOP Compliance API Service =============

export const sopComplianceService = {
  /**
   * Get compliance dashboard metrics
   * API: GET /api/v1/sop/analytics/dashboard
   *
   * @param industry Optional industry filter
   */
  getDashboard: async (industry?: string): Promise<ComplianceDashboardResponse> => {
    const params = new URLSearchParams();
    if (industry) params.append('industry', industry);

    const response = await apiClient.get<ComplianceDashboardResponse>(
      `/sop/analytics/dashboard?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Get compliance trends over time
   * API: GET /api/v1/sop/analytics/trends
   *
   * @param days Number of days (7-90)
   * @param industry Optional industry filter
   */
  getTrends: async (days: number = 30, industry?: string): Promise<ComplianceTrendsResponse> => {
    const params = new URLSearchParams();
    params.append('days', days.toString());
    if (industry) params.append('industry', industry);

    const response = await apiClient.get<ComplianceTrendsResponse>(
      `/sop/analytics/trends?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Get industry coverage statistics
   * API: GET /api/v1/sop/analytics/industry-coverage
   */
  getIndustryCoverage: async (): Promise<IndustryCoverageResponse> => {
    const response = await apiClient.get<IndustryCoverageResponse>(
      '/sop/analytics/industry-coverage'
    );
    return response.data;
  },

  /**
   * Get top performing SOPs
   * API: GET /api/v1/sop/analytics/top-sops
   *
   * @param limit Number of SOPs (1-20)
   */
  getTopSOPs: async (limit: number = 5): Promise<SOPPerformer[]> => {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());

    const response = await apiClient.get<SOPPerformer[]>(
      `/sop/analytics/top-sops?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Get bottom performing SOPs
   * API: GET /api/v1/sop/analytics/bottom-sops
   *
   * @param limit Number of SOPs (1-20)
   */
  getBottomSOPs: async (limit: number = 5): Promise<SOPPerformer[]> => {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());

    const response = await apiClient.get<SOPPerformer[]>(
      `/sop/analytics/bottom-sops?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Get KPI summary metrics
   * API: GET /api/v1/sop/kpi-summary
   *
   * @param industry Optional industry filter
   */
  getKPISummary: async (industry?: string): Promise<KPISummary> => {
    const params = new URLSearchParams();
    if (industry) params.append('industry', industry);

    const response = await apiClient.get<KPISummary>(
      `/sop/kpi-summary?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Get KPIs for a specific SOP
   * API: GET /api/v1/sop/{sop_id}/kpi
   *
   * @param sopId SOP template ID
   * @param includeInactive Include inactive KPIs
   */
  getSOPKPIs: async (sopId: string, includeInactive: boolean = false): Promise<SOPKPIMapping[]> => {
    const params = new URLSearchParams();
    params.append('include_inactive', includeInactive.toString());

    const response = await apiClient.get<SOPKPIMapping[]>(
      `/sop/${sopId}/kpi?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Get KPI performance for a SOP
   * API: GET /api/v1/sop/{sop_id}/kpi/performance
   *
   * @param sopId SOP template ID
   * @param fromDate Start date
   * @param toDate End date
   */
  getKPIPerformance: async (
    sopId: string,
    fromDate?: string,
    toDate?: string
  ): Promise<KPIPerformance[]> => {
    const params = new URLSearchParams();
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);

    const response = await apiClient.get<KPIPerformance[]>(
      `/sop/${sopId}/kpi/performance?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Validate SOP import data without importing
   * API: POST /api/v1/sop/import/validate
   *
   * @param data SOP import data
   */
  validateImport: async (data: SOPImportRequest): Promise<SOPValidateResponse> => {
    const response = await apiClient.post<SOPValidateResponse>(
      '/sop/import/validate',
      data
    );
    return response.data;
  },

  /**
   * Import SOPs from JSON
   * API: POST /api/v1/sop/import
   *
   * @param data SOP import data
   */
  importSOPs: async (data: SOPImportRequest): Promise<SOPImportResponse> => {
    const response = await apiClient.post<SOPImportResponse>(
      '/sop/import',
      data
    );
    return response.data;
  },

  /**
   * Get import history
   * API: GET /api/v1/sop/import/history
   *
   * @param limit Number of records
   */
  getImportHistory: async (limit: number = 20): Promise<SOPImportHistory[]> => {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());

    const response = await apiClient.get<SOPImportHistory[]>(
      `/sop/import/history?${params.toString()}`
    );
    return response.data;
  },
};

// ============= Utility Functions =============

/**
 * Get compliance score status (Vietnamese)
 */
export const getComplianceScoreStatus = (score: number): {
  status: 'excellent' | 'good' | 'warning' | 'critical';
  label: string;
  color: string;
} => {
  if (score >= 90) return { status: 'excellent', label: 'Xuất sắc', color: '#22c55e' };
  if (score >= 80) return { status: 'good', label: 'Tốt', color: '#3b82f6' };
  if (score >= 70) return { status: 'warning', label: 'Cần cải thiện', color: '#eab308' };
  return { status: 'critical', label: 'Quan trọng', color: '#ef4444' };
};

/**
 * Get violation severity color
 */
export const getViolationSeverityColor = (severity: 'critical' | 'high' | 'medium' | 'low'): string => {
  switch (severity) {
    case 'critical': return '#ef4444';
    case 'high': return '#f97316';
    case 'medium': return '#eab308';
    case 'low': return '#22c55e';
    default: return '#6b7280';
  }
};

/**
 * Get KPI status (Vietnamese)
 */
export const getKPIStatus = (status: string): {
  label: string;
  color: string;
} => {
  switch (status) {
    case 'GOOD': return { label: 'Đạt', color: '#22c55e' };
    case 'WARNING': return { label: 'Cảnh báo', color: '#eab308' };
    case 'CRITICAL': return { label: 'Quan trọng', color: '#ef4444' };
    case 'NO_DATA': return { label: 'Chưa có dữ liệu', color: '#6b7280' };
    default: return { label: status, color: '#6b7280' };
  }
};

/**
 * Format percentage for display
 */
export const formatPercent = (value: number, decimals: number = 1): string => {
  return `${value.toFixed(decimals)}%`;
};

/**
 * Format date for Vietnamese display
 */
export const formatDateVN = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('vi-VN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

/**
 * Format datetime for Vietnamese display
 */
export const formatDateTimeVN = (dateString: string): string => {
  return new Date(dateString).toLocaleString('vi-VN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default sopComplianceService;
